/** Pay on Account: ask the firm for it, or see where the request is up to. */

import { errorMessage, post } from "@taxi/mobile-web/api";
import { phone } from "@taxi/mobile-web/format";
import { BriefcaseIcon } from "@taxi/mobile-web/icons";
import { Button, Card, Field, Notice, TextArea, TopBar, useToast } from "@taxi/mobile-web/ui";
import { type FormEvent, useState } from "react";
import { useMe, useSession } from "../lib/session";
import type { Account } from "../lib/types";

export function AccountRequestScreen() {
  const me = useMe();
  const { refreshMe, firm, firmPhone } = useSession();
  const toast = useToast();
  const [billingName, setBillingName] = useState(me.name);
  const [billingEmail, setBillingEmail] = useState(me.email ?? "");
  const [billingAddress, setBillingAddress] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = me.account?.status;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post<Account>("/customer/account-request", {
        billingName: billingName.trim(),
        billingEmail: billingEmail.trim(),
        billingAddress: billingAddress.trim() || null,
        note: note.trim() || null,
      });
      await refreshMe();
      toast("Thanks, we'll be in touch");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="account-request">
      <TopBar title="Pay on Account" back="/account" />
      <div className="screen-body">
        <Card className="explainer">
          <BriefcaseIcon />
          <p>
            Book now, pay later: trips go on an invoice, usually monthly, which you pay by bank
            transfer. For businesses, hotels, schools and regulars.
          </p>
        </Card>
        {status === "approved" ? (
          <Notice tone="success">
            Your account is set up. Choose Pay on Account when you book.
          </Notice>
        ) : status === "requested" ? (
          <Notice tone="info">
            Thanks for asking. The office will check and let you know. Questions? Call{" "}
            {phone(firmPhone)}.
          </Notice>
        ) : (
          <form onSubmit={submit} className="stack">
            {status === "declined" && (
              <Notice tone="warning">
                We couldn't set up an account last time. Call {phone(firmPhone)} to talk it through.
              </Notice>
            )}
            {status === "removed" && (
              <Notice tone="warning">Your account was closed. You can ask again.</Notice>
            )}
            <Field
              label="Name for the invoices"
              value={billingName}
              onChange={(e) => setBillingName(e.target.value)}
              required
            />
            <Field
              label="Email for the invoices"
              type="email"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              required
            />
            <TextArea
              label="Billing address"
              value={billingAddress}
              maxLength={300}
              onChange={(e) => setBillingAddress(e.target.value)}
            />
            <TextArea
              label="Anything we should know? (optional)"
              placeholder="e.g. who'll be travelling, roughly how often"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
            />
            {error && <Notice tone="error">{error}</Notice>}
            <Button
              type="submit"
              loading={busy}
              disabled={!billingName.trim() || !billingEmail.trim()}
            >
              Ask {firm.name} for an account
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
